import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Row } from 'saladsoft-primitive';

const Select = forwardRef(
  ({
    title,
    style,
    containerStyle,
    fullWidth,
    defaultKey,
    items,
    placeHolder,
    useRound = true,
    onChange,
    className,
    itemBoxStyle,
  }: {
    title?: string;
    containerStyle?: React.CSSProperties;
    itemBoxStyle?: React.CSSProperties;
    fullWidth?: boolean;
    defaultKey?: any;
    items?: Record<string, any>[];
    placeHolder?: string;
    useRound?: boolean;
    className?: string;
    style?: React.CSSProperties;
    onChange?: (value: Record<string, any>) => void;
  }) => {
    const containerRef = useRef<any>(null);
    const [showItems, setShowItems] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<Record<string, any>>();

    const handleClickItem = (item: Record<string, any>, event: React.MouseEvent) => {
      event.stopPropagation(); // 이벤트 전파 막기
      setSelectedItem(item);
      setShowItems(false); // 드롭다운 닫기
      onChange?.(item);
    };

    const handleClickOutside = (event: any) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowItems(false);
      }
    };

    useEffect(() => {
      if (items && defaultKey) {
        setSelectedItem(items?.find((item) => item?.key === defaultKey));
      } else if (defaultKey === '') {
        setSelectedItem(undefined);
      }
    }, [defaultKey, items]);

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <>
        {title && <div style={{ fontSize: 14, paddingBottom: 5 }}>{title} </div>}
        <FieldContainer
          className={className}
          useRound={useRound}
          ref={containerRef}
          style={{
            width: fullWidth ? '100%' : 'auto',
            maxWidth: 'fit-content',
            ...containerStyle,
          }}
          onClick={() => {
            setShowItems((prev) => !prev);
          }}
        >
          <Outer style={{ width: fullWidth ? '100%' : 'auto', ...style }}>
            <Row justifyContent="space-between">
              <div style={{ padding: useRound ? '0 20px' : '0 5px', flexGrow: 1 }}>
                {selectedItem ? selectedItem?.value : placeHolder || '항목을 선택 해주세요'}
              </div>
              <IconButton style={{ background: '#fff', borderRadius: useRound ? 36 : 6 }}>
                {showItems ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Row>
            {showItems && (
              <ItemBox customStyle={itemBoxStyle}>
                {items?.map((item) => (
                  <div
                    key={item?.key}
                    onClick={(event) => handleClickItem(item, event)} // 클릭 이벤트에 대한 처리
                  >
                    {item?.value}
                  </div>
                ))}
              </ItemBox>
            )}
          </Outer>
        </FieldContainer>
      </>
    );
  },
);

const FieldContainer = styled.div<{ useRound?: boolean }>`
  border-radius: ${({ useRound }: { useRound?: boolean }) => (useRound ? '36px' : '6px')};
  padding: 0px 3px;
  height: 50px;
  color: #656565;
  background: #f6f6f6;
  border: 2px solid transparent;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  &:hover {
    border: 2px solid rebeccapurple;
  }
`;

const IconButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Outer = styled.div`
  position: relative;
  min-width: 300px;
`;

const ItemBox = styled.div<{ customStyle?: React.CSSProperties }>`
  position: absolute;
  background: #f6f6f6;
  width: 100%;
  z-index: 999;
  top: 55px;
  box-shadow: 0px 0px 22.5px 0px rgba(144, 155, 169, 0.25) !important;
  ${({ customStyle }) => customStyle && { ...customStyle }}
  div {
    transition: background-color 0.3s ease;
    padding: 15px;
    &:hover {
      background-color: #fff; /* 원하는 hover 색상 */
    }
  }
`;

export default Select;
